import { initializeApp, getApps } from 'firebase/app';
import { getAuth, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import { getAnalytics, isSupported, type Analytics } from 'firebase/analytics';
import { getPerformance, type FirebasePerformance } from 'firebase/performance';
import { getRemoteConfig, fetchAndActivate } from 'firebase/remote-config';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Avoid re-initialization during Vite HMR
export const firebaseApp =
  getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

export const firebaseAuth = getAuth(firebaseApp);
export const googleProvider = new GoogleAuthProvider();

export const microsoftProvider = new OAuthProvider('microsoft.com');
microsoftProvider.setCustomParameters({ prompt: 'select_account' });

// signInWithPopup relies on a popup window round-tripping through
// authDomain/__/auth/handler and postMessage-ing the result back to the
// opener. Mobile browsers routinely abort that popup navigation
// (ERR_CONNECTION_ABORTED), so mobile callers should use signInWithRedirect
// instead and pick up the result via getRedirectResult() on next mount.
export function isMobileAuthDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

// Analytics — browser only, requires measurementId
let _analytics: Analytics | null = null;
export const getFirebaseAnalytics = () => _analytics;

isSupported().then((ok) => {
  if (ok && import.meta.env.VITE_FIREBASE_MEASUREMENT_ID) {
    _analytics = getAnalytics(firebaseApp);
  }
});

// Performance Monitoring
let _perf: FirebasePerformance | null = null;
export const getFirebasePerf = () => _perf;

try {
  _perf = getPerformance(firebaseApp);
} catch {
  // Silently skip if perf SDK unavailable (e.g. blocked by adblocker)
}

// Remote Config with sensible defaults
export const remoteConfig = getRemoteConfig(firebaseApp);
remoteConfig.settings.minimumFetchIntervalMillis = import.meta.env.PROD
  ? 3_600_000  // 1 hour in production
  : 10_000;    // 10 seconds in development

remoteConfig.defaultConfig = {
  maintenance_mode: false,
  max_retry_attempts: 3,
  enable_load_tester: true,
  banner_message: '',
};

// Fetch and activate remote config on startup
fetchAndActivate(remoteConfig).catch(() => {
  // Remote config is non-critical — fail silently
});
